import { useArticleStore } from '~/store/article';
import type { Braze, PublicationContext } from '~/types';
import { getBrazeId, getMaTracking } from '~/utils/utils';

const PAGE_TYPE = {
  ARTICLE: 'article',
  LIST: 'list',
  SINGLE: 'single',
  FRONT: 'frontpage',
  TAG: 'tagpage',
  CATEGORY: 'categorypage',
  SECTION: 'sectionpage'
} as const;

export default defineNuxtPlugin((nuxt) => {
  const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;
  const brazeConfig = rtConfPub.braze;
  const isThresholdEnabled = (brazeConfig: Braze) => {
    return !!(brazeConfig && Object.keys(brazeConfig).length > 0 && brazeConfig?.options?.threshold);
  };

  if (isThresholdEnabled(brazeConfig)) {
    return;
  }

  const brazeRules = brazeConfig?.options?.rules || [];
  const returns = brazeConfig?.options?.returns;
  let currentSessionTime: { type: string; time: number } = { type: '', time: 0 };
  const LISTPAGES = [PAGE_TYPE.FRONT, PAGE_TYPE.TAG, PAGE_TYPE.SECTION, PAGE_TYPE.CATEGORY];
  let pageType = '';

  const updateTimeTracking = (pageType: string) => {
    const maTracking = getMaTracking();
    const now = Date.now();
    const diffInSeconds = currentSessionTime.time ? (now - currentSessionTime.time) / 1000 : 0;

    if (currentSessionTime.type === PAGE_TYPE.SINGLE) {
      maTracking.single = {
        ...maTracking?.single,
        time: (maTracking?.single?.time || 0) + diffInSeconds
      };
    } else if (currentSessionTime.type === PAGE_TYPE.LIST) {
      maTracking.list = {
        ...maTracking?.list,
        time: (maTracking?.list?.time || 0) + diffInSeconds
      };
    }
    currentSessionTime = { type: pageType, time: now };
    localStorage.setItem('ma_tracking', JSON.stringify(maTracking));
    console.log(`User time updated: ${JSON.stringify(maTracking)}`);
  };

  const updateViewTracking = (pageType: string, articleId = '') => {
    const maTracking = getMaTracking();

    if (pageType === PAGE_TYPE.SINGLE) {
      const views = maTracking?.single?.views?.includes(articleId)
        ? maTracking?.single?.views
        : Array.isArray(maTracking?.single?.views) ? [...maTracking?.single?.views, articleId] : [articleId];
      maTracking.single = {
        ...maTracking?.single,
        views
      };
    } else if (pageType === PAGE_TYPE.LIST) {
      maTracking.list = {
        ...maTracking?.list,
        views: (maTracking?.list?.views || 0) + 1
      };
    }

    localStorage.setItem('ma_tracking', JSON.stringify(maTracking));
    console.log(`User views updated: ${JSON.stringify(maTracking)}`);
  };

  const checkAndUpdateMaTracking = () => {
    const maTracking = getMaTracking();
    if (maTracking?.ma) {
      console.log('User has already met the rules for MA tracking');
      return;
    }
    const singlePageRule = brazeRules.map(rule => rule.single);
    const listPageRule = brazeRules.map(rule => rule.list);
    // check if the user has met one of the rules in singlePageRule and listPageRule
    const singlePageMet = singlePageRule.some((rule) => {
      return (
        maTracking.single.time >= rule.time &&
        maTracking.single.views.length >= rule.views
      );
    });
    const listPageMet = listPageRule.some((rule) => {
      return (
        maTracking.list.time >= rule.time &&
        maTracking.list.views >= rule.views
      );
    });
    if (returns && singlePageMet && listPageMet) {
      const currentReturns = maTracking.returns;

      if (currentReturns && currentReturns >= returns) {
        const ttl = brazeConfig?.options?.ttl || 0;
        localStorage.setItem('ma_tracking', JSON.stringify({ ma: true, expiry: Date.now() + ttl * 1000 }));
      } else {
        const updatedReturns = currentReturns ? currentReturns + 1 : 1;
        localStorage.setItem('ma_tracking', JSON.stringify({ returns: updatedReturns }));
      }
      currentSessionTime = { type: '', time: 0 };
    }
  };

  nuxt.hook('cpp:data:loaded', ({ type }: { type: string }) => {
    const brazeId = getBrazeId('sub-user-data');
    if (!brazeRules || !brazeRules.length) {
      return;
    } else if (brazeId) {
      return;
    }
    const maTracking = getMaTracking();
    if (maTracking?.ma) {
      console.log('User has already met the rules for MA tracking');
      return;
    }

    pageType = type;
    if (type === PAGE_TYPE.ARTICLE) {
      const articleStore = useArticleStore();
      updateViewTracking(PAGE_TYPE.SINGLE, articleStore.currentArticle.id);
      if (!currentSessionTime.time) {
        currentSessionTime = { type: PAGE_TYPE.SINGLE, time: Date.now() };
      } else {
        updateTimeTracking(PAGE_TYPE.SINGLE);
      }
    } else if (LISTPAGES.includes(type)) {
      updateViewTracking(PAGE_TYPE.LIST);
      if (!currentSessionTime.time) {
        currentSessionTime = { type: PAGE_TYPE.LIST, time: Date.now() };
      } else {
        updateTimeTracking(PAGE_TYPE.LIST);
      }
    }
  });

  nuxt.hook('app:beforeMount', () => {
    useEventListener(document, 'visibilitychange', () => {
      const brazeId = getBrazeId('sub-user-data');
      if (brazeId || !brazeRules || !brazeRules.length) {
        return;
      }
      const visibility = useDocumentVisibility();
      if (visibility.value === 'hidden') {
        const currentPageType = pageType === PAGE_TYPE.ARTICLE ? PAGE_TYPE.SINGLE : PAGE_TYPE.LIST;

        updateTimeTracking(currentPageType);
        checkAndUpdateMaTracking();
        currentSessionTime = { type: '', time: 0 };
      } else if (pageType === PAGE_TYPE.ARTICLE) {
        currentSessionTime.type = PAGE_TYPE.SINGLE;
        currentSessionTime.time = Date.now();
      } else if (LISTPAGES.includes(pageType)) {
        currentSessionTime.type = PAGE_TYPE.LIST;
        currentSessionTime.time = Date.now();
        updateViewTracking(PAGE_TYPE.LIST);
      }
    });
  });
});
